// src/pages/UserManagement.tsx - Admin-only user management with Viewer Role Support
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from '@/lib/supabase';
import type { Profile } from '@/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from "framer-motion";
import {
    Calendar,
    Eye,
    Mail,
    Search,
    Shield,
    Trash2,
    User,
    UserCheck,
    UserCog,
    X
} from "lucide-react";
import { useState, type ChangeEvent } from "react";
import { useAuth } from "../contexts/AuthContext";

interface UpdateRoleParams {
    userId: string
    newRole: string
}

export default function UserManagement() {
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const { profile } = useAuth();
    const queryClient = useQueryClient();

    // Modal State
    const [confirmationModal, setConfirmationModal] = useState<{
        isOpen: boolean;
        type: 'role' | 'delete' | null;
        title: string;
        message: React.ReactNode;
        onConfirm: () => void;
    }>({
        isOpen: false,
        type: null,
        title: '',
        message: '',
        onConfirm: () => { },
    });

    // Load all users
    const { data: users = [], isLoading } = useQuery({
        queryKey: ['users'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .order('created_at', { ascending: false });
            if (error) throw error;
            return (data || []) as Profile[];
        }
    });

    // Update user role mutation
    const updateUserRoleMutation = useMutation({
        mutationFn: async ({ userId, newRole }: UpdateRoleParams) => {
            console.log('Attempting to update role:', { userId, newRole });

            const { data, error } = await supabase
                .from('profiles')
                .update({ role: newRole })
                .eq('id', userId)
                .select()
                .single();

            console.log('Supabase response:', { data, error });

            if (error) {
                console.error('Supabase error details:', error);
                throw error;
            }
            return data as Profile;
        },
        onSuccess: (data) => {
            console.log('Role update successful:', data);
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
        onError: (error: Error) => {
            console.error('Mutation error:', error);
        }
    });

    // Delete user mutation (optional - be careful with this!)
    const deleteUserMutation = useMutation({
        mutationFn: async (userId: string) => {
            // First delete from profiles
            const { error: profileError } = await supabase
                .from('profiles')
                .delete()
                .eq('id', userId);
            if (profileError) throw profileError;

            // Note: Deleting from auth.users requires service role
            // This is typically done via a Supabase Edge Function
            return true;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        }
    });

    // Filter users
    const filteredUsers = users.filter(user => {
        const matchesSearch = !searchTerm ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.last_name?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRole = roleFilter === "all" || user.role === roleFilter;

        return matchesSearch && matchesRole;
    });

    const handleRoleChange = (userId: string, newRole: string) => {
        if (userId === profile?.id && newRole !== 'admin') {
            alert("Non puoi rimuovere i privilegi di amministratore dal tuo account!");
            return;
        }

        setConfirmationModal({
            isOpen: true,
            type: 'role',
            title: 'Conferma modifica ruolo',
            message: (
                <span>
                    Sei sicuro di voler cambiare il ruolo di questo utente a <span className="font-bold">"{newRole}"</span>?
                </span>
            ),
            onConfirm: async () => {
                try {
                    await updateUserRoleMutation.mutateAsync({ userId, newRole });
                    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
                } catch (error) {
                    console.error("Errore nell'aggiornamento del ruolo:", error);
                    // Handle error (maybe show another modal or toast)
                }
            }
        });
    };

    const handleDeleteUser = (userId: string, userEmail: string) => {
        if (userId === profile?.id) {
            alert("Non puoi eliminare il tuo account!");
            return;
        }

        setConfirmationModal({
            isOpen: true,
            type: 'delete',
            title: 'Conferma eliminazione',
            message: (
                <span>
                    Sei sicuro di voler eliminare l'utente <span className="font-bold">{userEmail}</span>?
                    <br />
                    <span className="text-sm text-slate-500 mt-2 block">Questa azione è irreversibile.</span>
                </span>
            ),
            onConfirm: async () => {
                try {
                    await deleteUserMutation.mutateAsync(userId);
                    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
                } catch (error) {
                    console.error("Errore nell'eliminazione utente:", error);
                }
            }
        });
    };

    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="p-4 md:p-8 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-slate-600">Caricamento utenti...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-800 mb-2">Gestione Utenti</h1>
                        <p className="text-slate-600">Gestisci gli utenti e i loro ruoli</p>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-slate-500">
                        <UserCog className="w-4 h-4" />
                        <span>Totale: {users.length} utenti</span>
                    </div>
                </div>

                {/* Filters */}
                <div className="grid lg:grid-cols-4 gap-6 mb-6">
                    <div className="lg:col-span-3">
                        <div className="relative">
                            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Cerca per nome, cognome o email..."
                                value={searchTerm}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                                className="pl-10 glass-effect border-0 shadow-lg"
                            />
                        </div>
                    </div>
                    <div>
                        <Select value={roleFilter} onValueChange={setRoleFilter}>
                            <SelectTrigger className="glass-effect border-0 shadow-lg">
                                <SelectValue placeholder="Filtra per ruolo" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti i ruoli</SelectItem>
                                <SelectItem value="admin">Amministratori</SelectItem>
                                <SelectItem value="operator">Operatori</SelectItem>
                                <SelectItem value="viewer">Visualizzatori</SelectItem>
                                <SelectItem value="registrato">Registrati</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left p-4 font-medium text-slate-700">
                                        <div className="flex items-center space-x-2">
                                            <User className="w-4 h-4" />
                                            <span>Utente</span>
                                        </div>
                                    </th>
                                    <th className="text-left p-4 font-medium text-slate-700">
                                        <div className="flex items-center space-x-2">
                                            <Mail className="w-4 h-4" />
                                            <span>Email</span>
                                        </div>
                                    </th>
                                    <th className="text-left p-4 font-medium text-slate-700">
                                        <div className="flex items-center space-x-2">
                                            <Shield className="w-4 h-4" />
                                            <span>Ruolo</span>
                                        </div>
                                    </th>
                                    <th className="text-left p-4 font-medium text-slate-700">
                                        <div className="flex items-center space-x-2">
                                            <Calendar className="w-4 h-4" />
                                            <span>Registrato</span>
                                        </div>
                                    </th>
                                    <th className="text-left p-4 font-medium text-slate-700">Azioni</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredUsers.map((user, index) => {
                                    return (
                                        <motion.tr
                                            key={user.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                            className="hover:bg-slate-50 transition-colors"
                                        >
                                            <td className="p-4">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center">
                                                        <span className="text-slate-600 font-semibold">
                                                            {user.first_name?.[0] || user.email?.[0] || 'U'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-slate-800">
                                                            {user.first_name && user.last_name
                                                                ? `${user.first_name} ${user.last_name}`
                                                                : 'Nome non impostato'
                                                            }
                                                        </div>
                                                        {user.id === profile?.id && (
                                                            <span className="text-xs text-blue-600 font-medium">(Tu)</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <span className="text-slate-800">{user.email}</span>
                                            </td>
                                            <td className="p-4">
                                                <Select
                                                    value={user.role}
                                                    onValueChange={(newRole) => handleRoleChange(user.id, newRole)}
                                                    disabled={updateUserRoleMutation.isPending}
                                                >
                                                    <SelectTrigger className={`w-40 ${user.role === 'admin'
                                                        ? 'bg-purple-50 border-purple-200'
                                                        : user.role === 'operator'
                                                            ? 'bg-blue-50 border-blue-200'
                                                            : user.role === 'viewer'
                                                                ? 'bg-green-50 border-green-200'
                                                                : 'bg-gray-50 border-gray-200'
                                                        }`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="admin">
                                                            <div className="flex items-center space-x-2">
                                                                <Shield className="w-4 h-4 text-purple-600" />
                                                                <span>Amministratore</span>
                                                            </div>
                                                        </SelectItem>
                                                        <SelectItem value="operator">
                                                            <div className="flex items-center space-x-2">
                                                                <User className="w-4 h-4 text-blue-600" />
                                                                <span>Operatore</span>
                                                            </div>
                                                        </SelectItem>
                                                        <SelectItem value="viewer">
                                                            <div className="flex items-center space-x-2">
                                                                <Eye className="w-4 h-4 text-green-600" />
                                                                <span>Visualizzatore</span>
                                                            </div>
                                                        </SelectItem>
                                                        <SelectItem value="registrato">
                                                            <div className="flex items-center space-x-2">
                                                                <UserCheck className="w-4 h-4 text-gray-600" />
                                                                <span>Registrato</span>
                                                            </div>
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </td>
                                            <td className="p-4">
                                                <span className="text-sm text-slate-600">
                                                    {user.created_at && formatDate(user.created_at)}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex space-x-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        disabled={user.id === profile?.id}
                                                        onClick={() => handleDeleteUser(user.id, user.email)}
                                                        className="hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {filteredUsers.length === 0 && (
                        <div className="p-8 text-center">
                            <UserCog className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-slate-600 mb-2">Nessun utente trovato</h3>
                            <p className="text-slate-500">Modifica i filtri di ricerca per trovare utenti.</p>
                        </div>
                    )}
                </div>

                {/* Stats */}
                <div className="grid md:grid-cols-5 gap-6 mt-8">
                    <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-200">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                                <Shield className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-slate-600 text-sm">Amministratori</p>
                                <p className="text-2xl font-bold text-slate-800">
                                    {users.filter(u => u.role === 'admin').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-200">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                                <User className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-slate-600 text-sm">Operatori</p>
                                <p className="text-2xl font-bold text-slate-800">
                                    {users.filter(u => u.role === 'operator').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-200">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                                <Eye className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-slate-600 text-sm">Visualizzatori</p>
                                <p className="text-2xl font-bold text-slate-800">
                                    {users.filter(u => u.role === 'viewer').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-200">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl flex items-center justify-center">
                                <UserCheck className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-slate-600 text-sm">Registrati</p>
                                <p className="text-2xl font-bold text-slate-800">
                                    {users.filter(u => u.role === 'registrato').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-lg border border-slate-200">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
                                <UserCog className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-slate-600 text-sm">Totale Utenti</p>
                                <p className="text-2xl font-bold text-slate-800">{users.length}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {confirmationModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-slate-800">{confirmationModal.title}</h2>
                            <button
                                onClick={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
                                className="p-1 hover:bg-slate-100 rounded transition-colors"
                            >
                                <X className="w-5 h-5 text-slate-500" />
                            </button>
                        </div>

                        <div className="p-6">
                            <p className="text-slate-700">
                                {confirmationModal.message}
                            </p>
                        </div>

                        <div className="p-6 border-t border-slate-200 flex gap-3">
                            <button
                                onClick={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
                                className="flex-1 px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors font-medium"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={confirmationModal.onConfirm}
                                disabled={updateUserRoleMutation.isPending || deleteUserMutation.isPending}
                                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed ${confirmationModal.type === 'delete'
                                    ? 'bg-red-600 hover:bg-red-700'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                            >
                                {(updateUserRoleMutation.isPending || deleteUserMutation.isPending)
                                    ? 'Elaborazione...'
                                    : 'Conferma'
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>

    );
}
